import { Skeleton } from "@/components/ui/skeleton";
import type {
  AgentPulseDataSnapshot,
  AgentPulseWonLostOpportunity,
} from "@forgepoint/shared";

interface WinsTableProps {
  opportunities: AgentPulseWonLostOpportunity[];
  pulseSnapshot: AgentPulseDataSnapshot | null;
  isLoading: boolean;
}

/**
 * Displays won and lost opportunities as a unified ledger with per-row status
 * indicators, summary counters, and aligned columns. Designed for the Agent
 * Pulse dashboard's full-width slot.
 * @param opportunities - Won/lost opportunities from the store
 * @param pulseSnapshot - Snapshot data frozen at pulse generation time
 * @param isLoading - Whether data is still being fetched
 * @returns A full-width opportunity ledger
 */
export function WinsTable({
  opportunities,
  pulseSnapshot,
  isLoading,
}: WinsTableProps) {
  const won = opportunities.filter((o) => o.status === "ACCEPTED");
  const lost = opportunities.filter((o) => o.status === "LOST");

  const totalWonValue = won.reduce(
    (sum, o) => sum + (o.amount ? parseFloat(o.amount) : 0),
    0,
  );

  const totalLostValue = lost.reduce(
    (sum, o) => sum + (o.amount ? parseFloat(o.amount) : 0),
    0,
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Won / Lost Opportunities
        </h3>
        <span className="font-mono text-[11px] text-muted-foreground/70">
          Updated every 30s
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        {isLoading ? (
          <LoadingSkeleton />
        ) : opportunities.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <SummaryStrip
              wonCount={won.length}
              lostCount={lost.length}
              totalWonValue={totalWonValue}
              totalLostValue={totalLostValue}
              pulseSnapshot={pulseSnapshot}
            />
            <OpportunityLedger won={won} lost={lost} />
          </>
        )}
      </div>
    </div>
  );
}

interface SummaryStripProps {
  wonCount: number;
  lostCount: number;
  totalWonValue: number;
  totalLostValue: number;
  pulseSnapshot: AgentPulseDataSnapshot | null;
}

/**
 * Compact header strip showing won/lost counts, combined values,
 * and deltas compared against the last pulse snapshot.
 * @param wonCount - Number of won opportunities
 * @param lostCount - Number of lost opportunities
 * @param totalWonValue - Total dollar value of won opportunities
 * @param totalLostValue - Total dollar value of lost opportunities
 * @param pulseSnapshot - Snapshot data frozen at pulse generation time
 * @returns A dense summary row above the ledger
 */
function SummaryStrip({
  wonCount,
  lostCount,
  totalWonValue,
  totalLostValue,
  pulseSnapshot,
}: SummaryStripProps) {
  const hasSnapshot = pulseSnapshot?.wonOpportunitiesCount !== undefined;
  const wonCountDelta = hasSnapshot
    ? wonCount - (pulseSnapshot?.wonOpportunitiesCount ?? 0)
    : null;
  const lostCountDelta = hasSnapshot
    ? lostCount - (pulseSnapshot?.lostOpportunitiesCount ?? 0)
    : null;

  return (
    <div className="flex items-center gap-6 border-b bg-muted/30 px-6 py-2.5">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-[7px] w-[7px] rounded-full bg-emerald-500" />
        <span className="text-[13px] font-medium">{wonCount} won</span>
        {totalWonValue > 0 && (
          <span className="font-mono text-[12px] text-emerald-600 dark:text-emerald-400">
            {formatCurrency(totalWonValue)}
          </span>
        )}
        {wonCountDelta !== null && wonCountDelta !== 0 && (
          <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-400">
            ({wonCountDelta > 0 ? "+" : ""}
            {wonCountDelta} since last pulse)
          </span>
        )}
      </div>
      {lostCount > 0 && (
        <div className="flex items-center gap-2">
          <span className="inline-flex h-[7px] w-[7px] rounded-full bg-red-400" />
          <span className="text-[13px] font-medium text-foreground/70">
            {lostCount} lost
          </span>
          {totalLostValue > 0 && (
            <span className="font-mono text-[12px] text-red-500/80 dark:text-red-400/80">
              {formatCurrency(totalLostValue)}
            </span>
          )}
          {lostCountDelta !== null && lostCountDelta !== 0 && (
            <span className="font-mono text-[10px] text-red-500/80 dark:text-red-400/80">
              ({lostCountDelta > 0 ? "+" : ""}
              {lostCountDelta} since last pulse)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface OpportunityLedgerProps {
  won: AgentPulseWonLostOpportunity[];
  lost: AgentPulseWonLostOpportunity[];
}

/**
 * Single unified table for won and lost opportunities. Status is shown via
 * a left-edge accent and subtle row background. Won rows appear first.
 * @param props - Separated won and lost opportunity arrays
 * @returns A single table with consistent column alignment
 */
function OpportunityLedger({ won, lost }: OpportunityLedgerProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed text-[13.5px]">
        <colgroup>
          <col style={{ width: "3px" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "22%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "12%" }} />
        </colgroup>
        <thead>
          <tr className="border-b">
            <th />
            <th className="py-2.5 pl-5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Project
            </th>
            <th className="py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Company
            </th>
            <th className="py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Contact
            </th>
            <th className="py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Sales Rep
            </th>
            <th className="py-2.5 pr-6 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {won.map((opp) => (
            <OpportunityRow key={opp.id} opp={opp} variant="won" />
          ))}
          {lost.length > 0 && won.length > 0 && (
            <tr>
              <td colSpan={6} className="h-0">
                <div className="border-t border-dashed" />
              </td>
            </tr>
          )}
          {lost.map((opp) => (
            <OpportunityRow key={opp.id} opp={opp} variant="lost" />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface OpportunityRowProps {
  opp: AgentPulseWonLostOpportunity;
  variant: "won" | "lost";
}

/**
 * Single opportunity row with a left-edge accent bar and variant-specific styling.
 * @param props - Opportunity data and won/lost variant
 * @returns A table row with status accent
 */
function OpportunityRow({ opp, variant }: OpportunityRowProps) {
  const isWon = variant === "won";

  return (
    <tr className="group transition-colors hover:bg-muted/40">
      <td className={`w-[3px] ${isWon ? "bg-emerald-500" : "bg-red-400"}`} />
      <td className="py-3 pl-5 font-medium">{opp.name}</td>
      <td className="py-3 text-foreground/70">{opp.companyName ?? "—"}</td>
      <td className="py-3 text-foreground/70">{opp.contactName ?? "—"}</td>
      <td className="py-3 text-foreground/70">{opp.salesRepName ?? "—"}</td>
      <td
        className={`py-3 pr-6 text-right font-mono tabular-nums ${
          isWon
            ? "font-semibold text-emerald-600 dark:text-emerald-400"
            : "text-red-500/80 dark:text-red-400/80"
        }`}
      >
        {opp.amount ? formatCurrency(parseFloat(opp.amount)) : "—"}
      </td>
    </tr>
  );
}

/**
 * Formats a number as USD currency (e.g. $1,860,000 -> $1.86M).
 * @param value - The numeric value to format
 * @returns Formatted currency string
 */
function formatCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return `$${value.toLocaleString()}`;
}

function EmptyState() {
  return (
    <div className="px-6 py-12 text-center">
      <p className="text-sm font-medium text-foreground/70">
        No won or lost opportunities this period
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Won and lost deals will appear here as they are recorded.
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3 p-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-16" />
      </div>
      <div className="space-y-2 pt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
